import type { Point } from 'geojson'
import type { MapboxMap } from 'react-map-gl'

/** Continental Europe overview when no facility geo is available. */
export const ASSET_DIALOG_CREATE_CENTER: [number, number] = [10, 50]
export const ASSET_DIALOG_CREATE_ZOOM = 2

/**
 * Close-up around an existing asset point (Edit dialogs).
 * Aligns with main Assets map single-point framing, slightly pulled back for the dialog map size.
 */
export const ASSET_DIALOG_EDIT_ZOOM = 16

/** Create / edit-without-point: frame the facility base point. */
export const ASSET_DIALOG_FACILITY_ZOOM = 15

/** Max zoom when fitting several facility context pins after a facility switch. */
export const ASSET_DIALOG_CONTEXT_MAX_ZOOM = 15

/** Zoom animation after the camera has already jumped to the target center. */
export const ASSET_DIALOG_ZOOM_DURATION_MS = 350

export type AssetDialogMapMode = 'create' | 'edit'

export type AssetDialogCameraCancel = () => void

function isValidLngLat(coordinates: number[] | undefined): coordinates is [number, number, ...number[]] {
  return Boolean(
    coordinates &&
      coordinates.length >= 2 &&
      Number.isFinite(coordinates[0]) &&
      Number.isFinite(coordinates[1])
  )
}

function collectLngLats(points: Array<Point | null | undefined>): Array<[number, number]> {
  const out: Array<[number, number]> = []
  for (const point of points) {
    const coords = point?.coordinates
    if (isValidLngLat(coords)) {
      out.push([coords[0], coords[1]])
    }
  }
  return out
}

function asCenterTuple(center: { lng: number; lat: number } | [number, number]): [number, number] {
  if (Array.isArray(center)) {
    return [center[0], center[1]]
  }
  return [center.lng, center.lat]
}

/**
 * 1) Instant jump to the new center (focus), keeping current zoom.
 * 2) After the jump is committed to the map, animate zoom only.
 *
 * Calling jumpTo + easeTo in the same tick lets Mapbox merge them into one tween
 * that still looks like “zoom while on the old place, then pan”.
 */
function focusThenZoom(map: MapboxMap, center: [number, number], zoom: number): AssetDialogCameraCancel {
  map.stop()
  map.jumpTo({ center })

  let cancelled = false
  let outerRaf = 0
  let innerRaf = 0

  outerRaf = requestAnimationFrame(() => {
    innerRaf = requestAnimationFrame(() => {
      if (cancelled) {
        return
      }
      // Zoom only — center is already on the new facility.
      map.easeTo({
        zoom,
        duration: ASSET_DIALOG_ZOOM_DURATION_MS,
        essential: true,
      })
    })
  })

  return () => {
    cancelled = true
    cancelAnimationFrame(outerRaf)
    cancelAnimationFrame(innerRaf)
    map.stop()
  }
}

/**
 * Frame create / facility-switch (no editable pin): prefer facility + context pins,
 * then facility alone, else Europe overview.
 */
function frameFacilityContext(
  map: MapboxMap,
  facilityPoint?: Point | null,
  contextPoints?: Array<Point | null | undefined> | null
): AssetDialogCameraCancel {
  const coords = collectLngLats([facilityPoint, ...(contextPoints ?? [])])
  if (coords.length === 0) {
    map.stop()
    map.jumpTo({
      center: ASSET_DIALOG_CREATE_CENTER,
      zoom: ASSET_DIALOG_CREATE_ZOOM,
    })
    return () => undefined
  }

  if (coords.length === 1) {
    return focusThenZoom(map, coords[0], ASSET_DIALOG_FACILITY_ZOOM)
  }

  let minLng = coords[0][0]
  let maxLng = coords[0][0]
  let minLat = coords[0][1]
  let maxLat = coords[0][1]
  for (let i = 1; i < coords.length; i += 1) {
    const [lng, lat] = coords[i]
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
  }

  const bounds: [[number, number], [number, number]] = [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
  const camera = map.cameraForBounds(bounds, {
    padding: 48,
    maxZoom: ASSET_DIALOG_CONTEXT_MAX_ZOOM,
  })
  const center = camera?.center
    ? asCenterTuple(camera.center as { lng: number; lat: number } | [number, number])
    : ([(minLng + maxLng) / 2, (minLat + maxLat) / 2] as [number, number])
  const zoom =
    typeof camera?.zoom === 'number' && Number.isFinite(camera.zoom)
      ? camera.zoom
      : ASSET_DIALOG_FACILITY_ZOOM

  return focusThenZoom(map, center, zoom)
}

export function applyAssetDialogMapCamera(
  map: MapboxMap | undefined,
  mode: AssetDialogMapMode,
  point?: Point | null,
  facilityPoint?: Point | null,
  contextPoints?: Array<Point | null | undefined> | null
): AssetDialogCameraCancel {
  if (!map) {
    return () => undefined
  }

  const pointCoords = point?.coordinates
  if (mode === 'edit' && isValidLngLat(pointCoords)) {
    return focusThenZoom(map, [pointCoords[0], pointCoords[1]], ASSET_DIALOG_EDIT_ZOOM)
  }

  // Create, or edit after facility switch cleared the draft pin.
  return frameFacilityContext(map, facilityPoint, contextPoints)
}
